// The estate's RF log — every code any receiver heard, for the Super Admin
// "RF Log" page. Pure: rows from GetRfEventLog in, filtered rows and the
// "not configured yet" summary out.
//
// Why the page exists: a sensor installed today is a transmitter the server has
// heard but nobody has named. The server logs EVERY code (rfDevices.js — an
// unknown transmitter gets an unnamed RfDevice row on first sight), so "heard
// in this date range, still unnamed" is exactly the list of sensors waiting to
// be configured, with the receiver that heard them telling you where they are.
//
// ⚠️ A sensor nobody configured has no location of its own — its place is the
// RECEIVER's (ReceiverLocationName), which the server joins in since 2026-10-12.
// An older server omits those columns; the rows still work, place shows '—'.

import { eventByteOf, transmitterOf } from './rfCodes';
import { rfNumber } from './rfIdentify';
import { parseServerTime } from './firmwareInfo';
import { detectRfType } from './rfSignatures';

const str = (v) => (v == null ? '' : String(v)).trim();
const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };

/** One GetRfEventLog row -> the shape the page draws and filters. */
export function rfLogRowOf(raw) {
  const code = str(raw.RemoteValue);
  const transmitterId = num(raw.TransmitterId) || transmitterOf(code);
  const rfDeviceId = num(raw.RfDeviceId);
  const name = str(raw.RfDeviceName);
  return {
    id: str(raw.RfEventId) || `${raw.At}-${code}`,
    at: parseServerTime(raw.At),                 // epoch ms or null
    code,
    eventByte: eventByteOf(code),
    transmitterId,
    rfDeviceId,
    rfNumber: rfNumber(rfDeviceId),
    name,
    configured: !!(rfDeviceId && name),
    rfType: str(raw.RfType).toLowerCase(),
    meaning: str(raw.Meaning),
    receiverUno: str(raw.ReceiverUniqueNumber),
    receiverName: str(raw.ReceiverName),
    // The sensor's own place when it has one, else where it was heard.
    locationId: num(raw.RfLocationID) || num(raw.ReceiverLocationID) || 0,
    locationName: str(raw.LocationName) || str(raw.ReceiverLocationName),
    roomName: str(raw.RoomName) || str(raw.ReceiverRoomName),
    actionsFired: num(raw.ActionsFired),
    notified: num(raw.Notified),
    raw: str(raw.Raw),
  };
}

const ALL = '';

/**
 * filters: { search, receiver, location, type, meaning, status: ''|'new'|'configured' }
 * Every filter is optional; '' means "any".
 */
export function filterRfLog(rows, f = {}) {
  const q = str(f.search).toLowerCase();
  return (rows || []).filter((r) => {
    if (f.receiver && f.receiver !== ALL && r.receiverUno !== f.receiver) return false;
    if (f.location && f.location !== ALL && String(r.locationId) !== String(f.location)) return false;
    if (f.type && f.type !== ALL && (r.rfType || 'unknown') !== f.type) return false;
    if (f.meaning && f.meaning !== ALL && (r.meaning || '—') !== f.meaning) return false;
    if (f.status === 'new' && r.configured) return false;
    if (f.status === 'configured' && !r.configured) return false;
    if (q) {
      const hay = [r.name, r.rfNumber, r.code, String(r.transmitterId), r.receiverUno,
        r.receiverName, r.locationName, r.roomName, r.meaning, r.rfType].join(' ').toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
}

/** Distinct values for a dropdown: [{ value, label, count }] by label. */
export function rfLogOptions(rows, key) {
  const m = new Map();
  (rows || []).forEach((r) => {
    let value;
    let label;
    if (key === 'receiver') { value = r.receiverUno; label = r.receiverName ? `${r.receiverName} (${r.receiverUno})` : r.receiverUno; }
    else if (key === 'location') { value = String(r.locationId); label = r.locationName || (r.locationId ? `Location ${r.locationId}` : 'No location'); }
    else if (key === 'type') { value = r.rfType || 'unknown'; label = value; }
    else { value = r.meaning || '—'; label = value; }
    if (!value && value !== '0') return;
    const hit = m.get(value) || { value, label, count: 0 };
    hit.count += 1;
    m.set(value, hit);
  });
  return [...m.values()].sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Transmitters heard in the rows that no one has named — the sensors waiting
 * to be configured. One entry per transmitter: when first and last heard, how
 * many presses, the event bytes seen, where (receiver), and — with a signature
 * library — what it PROBABLY is (always a guess; the installer confirms).
 */
export function unconfiguredTransmitters(rows, sig = null) {
  const m = new Map();
  (rows || []).forEach((r) => {
    if (r.configured || !r.transmitterId) return;
    const t = m.get(r.transmitterId) || {
      transmitterId: r.transmitterId,
      rfDeviceId: r.rfDeviceId,
      rfNumber: r.rfNumber,
      firstAt: r.at,
      lastAt: r.at,
      count: 0,
      codes: new Set(),
      eventBytes: new Set(),
      receivers: new Map(),
      locationId: r.locationId,
      locationName: r.locationName,
    };
    t.count += 1;
    t.codes.add(r.code);
    t.eventBytes.add(r.eventByte);
    if (r.at != null && (t.firstAt == null || r.at < t.firstAt)) t.firstAt = r.at;
    if (r.at != null && (t.lastAt == null || r.at > t.lastAt)) t.lastAt = r.at;
    if (!t.rfDeviceId && r.rfDeviceId) { t.rfDeviceId = r.rfDeviceId; t.rfNumber = r.rfNumber; }
    if (r.receiverUno) t.receivers.set(r.receiverUno, r.receiverName || r.receiverUno);
    if (!t.locationName && r.locationName) { t.locationName = r.locationName; t.locationId = r.locationId; }
    m.set(r.transmitterId, t);
  });
  return [...m.values()]
    .map((t) => {
      const guess = sig ? detectRfType([...t.eventBytes], sig) : null;
      return {
        ...t,
        codes: [...t.codes],
        eventBytes: [...t.eventBytes].sort((a, b) => a - b),
        receivers: [...t.receivers.entries()].map(([uno, name]) => ({ uno, name })),
        guessType: guess ? guess.rfType : '',
      };
    })
    .sort((a, b) => (b.lastAt || 0) - (a.lastAt || 0));
}

/** 'YYYY-MM-DD' in the viewer's local calendar (the IST wall clock in India). */
export function ymd(d = new Date()) {
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
